import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';

export default function Search() {
  const { query: initQuery } = useParams();
  const navigate = useNavigate();

  const [q, setQ]             = useState(initQuery ? decodeURIComponent(initQuery) : '');
  const [results, setResults]  = useState([]);
  const [loading, setLoading]  = useState(false);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // Keyboard shortcut: / to focus
  useEffect(() => {
    function onKey(e) {
      if (e.key === '/' && document.activeElement !== inputRef.current) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Run search when URL query param changes
  useEffect(() => {
    if (initQuery) runSearch(decodeURIComponent(initQuery));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initQuery]);

  function runSearch(val) {
    const v = val.trim();
    if (v.length < 2) { setResults([]); setSearched(false); setLoading(false); return; }
    setLoading(true);
    setSearched(true);
    api.playerSearch(v)
      .then(data => { setResults(data); setLoading(false); })
      .catch(() => setLoading(false));
  }

  function handleChange(e) {
    const val = e.target.value;
    setQ(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(val), 300);
  }

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <div className="page-header-title">Player Search</div>
          <div className="page-header-sub">Find any tracked player by username</div>
        </div>
      </div>

      <div className="search-bar">
        <span className="search-icon">🔍</span>
        <input
          ref={inputRef}
          className="search-input"
          placeholder="Search players… (min 2 chars)"
          value={q}
          onChange={handleChange}
          autoComplete="off"
          spellCheck="false"
        />
        <span className="search-hint" title="Press / to focus">/</span>
      </div>

      {loading && <div className="page-center" style={{ padding: 20 }}><div className="spinner spinner-sm" /></div>}

      {!loading && searched && results.length === 0 && (
        <div className="empty-msg">No players found for "{q}".</div>
      )}

      {!loading && !searched && (
        <div className="empty-msg">Type at least 2 characters to search.</div>
      )}

      {!loading && results.length > 0 && (
        <>
          <div style={{ color: 'var(--muted)', fontSize: '.82rem', marginBottom: 10 }}>
            {results.length} result{results.length !== 1 ? 's' : ''}
          </div>
          <div className="search-results">
            {results.map(r => (
              <div
                key={r.robloxId}
                className="search-result-card"
                onClick={() => navigate(`/player/${r.robloxId}`)}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'var(--bg3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, color: 'var(--muted)', flexShrink: 0,
                }}>
                  {r.username?.[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="search-result-name">{r.username}</div>
                  <div className="search-result-meta">
                    {r.clanName ? `${r.clanName} · ` : ''}ID: {r.robloxId}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {r.clanName && <span className="badge badge-blue">{r.clanName}</span>}
                  <a
                    href={`https://www.roblox.com/users/${r.robloxId}/profile`}
                    target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{ color: 'var(--muted)', fontSize: '.85rem' }}
                  >↗</a>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
